import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, ShieldQuestion, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { humanizeArgKey, summarizeActionArgs } from '@/lib/ai-assistant/summarizeActionArgs';
import type { AssistantAction, AssistantActionPreview } from '@/lib/ai-assistant/types';

const DESTRUCTIVE = /^(delete_|cancel_)/;
// Short enum-like values get a translation when one exists (vertical, tiktok…).
const TRANSLATABLE_VALUE = /^[a-z_]{2,24}$/;

function PreviewBlock({ preview }: { preview: AssistantActionPreview }) {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!preview.image_url && !imageFailed;
  return (
    <div className="mt-2 flex gap-3 rounded-md bg-muted/40 p-2" data-testid="assistant-action-preview">
      {showImage && (
        <img
          src={preview.image_url!}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
          className="aspect-video w-28 shrink-0 rounded object-cover"
        />
      )}
      <div className="min-w-0 flex-1 self-center">
        {preview.kind && (
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t(`aiAssistant.previewKinds.${preview.kind}`, { defaultValue: '' })}
          </p>
        )}
        {preview.title && <p className="line-clamp-2 text-sm font-medium leading-snug">{preview.title}</p>}
        {preview.subtitle && <p className="truncate text-xs text-muted-foreground">{preview.subtitle}</p>}
      </div>
    </div>
  );
}

interface Props {
  action: AssistantAction;
  canResolve: boolean;
  resolving: boolean;
  onResolve: (actionId: string, approve: boolean) => void;
}

// The only way an assistant action runs: the user clicks Confirm here.
export function AssistantActionCard({ action, canResolve, resolving, onResolve }: Props) {
  const { t } = useTranslation();
  const preview = action.preview;
  const rows = summarizeActionArgs(action.args, { hideIds: !!preview });
  const destructive = DESTRUCTIVE.test(action.tool_name);
  const label = t(`aiAssistant.tools.${action.tool_name}`, { defaultValue: action.title });
  const pending = action.status === 'pending';
  const formatValue = (value: string) => (TRANSLATABLE_VALUE.test(value) || value === 'true' || value === 'false'
    ? t(`aiAssistant.values.${value}`, { defaultValue: value })
    : value);

  return (
    <div
      data-testid="assistant-action-card"
      className={cn(
        'rounded-lg border bg-card p-3 text-sm',
        destructive ? 'border-destructive/50' : 'border-primary/40',
      )}
    >
      <div className="flex items-start gap-2">
        <ShieldQuestion className={cn('mt-0.5 h-4 w-4 shrink-0', destructive ? 'text-destructive' : 'text-primary')} />
        <p className="min-w-0 flex-1 font-medium">{t('aiAssistant.action.title', { action: label })}</p>
      </div>
      {preview && <PreviewBlock preview={preview} />}
      {rows.length > 0 && (
        <dl className="mt-2 space-y-1 text-xs">
          {rows.map((row) => (
            <div key={row.key} className="flex gap-2">
              <dt className="shrink-0 text-muted-foreground">
                {t(`aiAssistant.args.${row.key}`, { defaultValue: humanizeArgKey(row.key) })}
              </dt>
              <dd className="min-w-0 break-words font-medium">{formatValue(row.value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {pending && canResolve ? (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant={destructive ? 'destructive' : 'default'}
            disabled={resolving}
            onClick={() => onResolve(action.id, true)}
            data-testid="assistant-action-confirm"
          >
            {resolving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            {t('aiAssistant.action.confirm')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={resolving}
            onClick={() => onResolve(action.id, false)}
            data-testid="assistant-action-cancel"
          >
            <X className="h-3.5 w-3.5" />
            {t('aiAssistant.action.cancel')}
          </Button>
        </div>
      ) : (
        <Badge variant="outline" className="mt-3 text-[11px] font-normal">
          {t(`aiAssistant.action.status.${action.status}`)}
        </Badge>
      )}
    </div>
  );
}
